import { useCallback, useRef } from "react";

/**
 * Custom hook for performance optimizations including debouncing and throttling
 */
export const usePerformanceOptimizations = () => {
  const timeouts = useRef(new Map());
  const animationFrames = useRef(new Map());

  // Debounce function for expensive operations
  const debounce = useCallback((func, delay, key = "default") => {
    return (...args) => {
      // Clear existing timeout for this key
      if (timeouts.current.has(key)) {
        clearTimeout(timeouts.current.get(key));
      }

      // Set new timeout
      const timeoutId = setTimeout(() => {
        func(...args);
        timeouts.current.delete(key);
      }, delay);

      timeouts.current.set(key, timeoutId);
    };
  }, []);

  // Throttle function using requestAnimationFrame for smooth animations
  const throttleRAF = useCallback((func, key = "default") => {
    return (...args) => {
      // Cancel existing frame for this key
      if (animationFrames.current.has(key)) {
        cancelAnimationFrame(animationFrames.current.get(key));
      }

      // Schedule new frame
      const frameId = requestAnimationFrame(() => {
        func(...args);
        animationFrames.current.delete(key);
      });

      animationFrames.current.set(key, frameId);
    };
  }, []);

  // Cleanup function to clear all pending operations
  const cleanup = useCallback(() => {
    // Clear all timeouts
    timeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeouts.current.clear();

    // Cancel all animation frames
    animationFrames.current.forEach((frameId) => cancelAnimationFrame(frameId));
    animationFrames.current.clear();
  }, []);

  // Optimized setState for expensive state updates
  const optimizedSetState = useCallback(
    (setState, delay = 100) => {
      return debounce(setState, delay, "setState");
    },
    [debounce]
  );

  // Batch DOM updates for better performance
  const batchDOMUpdates = useCallback(
    (updates) => {
      return throttleRAF(() => {
        // Use document.documentElement.style.setProperty for better performance
        updates.forEach((update) => {
          if (typeof update === "function") {
            update();
          }
        });
      }, "domUpdates");
    },
    [throttleRAF]
  );

  return {
    debounce,
    throttleRAF,
    optimizedSetState,
    batchDOMUpdates,
    cleanup,
  };
};
